import { css, SerializedStyles } from '@emotion/react';
import styled from '@emotion/styled';
import { AdminIcon } from 'src/components/Header/LHSMenu/icons/AdminIcon';
import { BonusEngineIcon } from 'src/components/Header/LHSMenu/icons/BonusEngineIcon';
import { CasinoIcon } from 'src/components/Header/LHSMenu/icons/CasinoIcon';
import { CMSIcon } from 'src/components/Header/LHSMenu/icons/CMSIcon';
import { ComplianceIcon } from 'src/components/Header/LHSMenu/icons/ComplianceIcon';
import { LogoutIcon } from 'src/components/Header/LHSMenu/icons/LogoutIcon';
import { NotificationIcon } from 'src/components/Header/LHSMenu/icons/NotificationIcon';
import { ReportingIcon } from 'src/components/Header/LHSMenu/icons/ReportingIcon';
import { TradingIcon } from 'src/components/Header/LHSMenu/icons/TradingIcon';
import { UserIcon } from 'src/components/Header/LHSMenu/icons/UserIcon';
import { PlayBookIcon } from 'src/components/Header/PlaybookLogo';
import Link from 'src/components/Link/Link';
import { scrollbar } from 'src/assets/colors';
import { BellIcon } from 'lucide-react';

/* stylelint-disable */
export const SideMenuContainer = styled('nav', { label: 'SideMenuContainer' })`
    background: hsl(var(--bg-sidebar-primary));
    display: flex;
    flex-flow: column nowrap;
    flex: 0 0 88px;
    font-family: var(--font-family-tertiary);
    height: 100vh;
    padding: 16px 0;
    width: 88px;
    z-index: 2;
`;

interface MenuButtonWrapperType {
    isActive?: boolean;
    isDisabled?: boolean;
}

const setMenuItem = (isActive?: boolean, isDisabled?: boolean): SerializedStyles => {
    return css`
        align-items: center;
        border-radius: 6px;
        ${isActive === true ? 'color: hsl(var(--menu-secondary));' : 'color: hsl(var(--menu-primary));'}
        cursor: ${isDisabled === true ? 'default' : 'pointer'};
        display: flex;
        flex-flow: column nowrap;
        font-size: 10px;
        height: 62px;
        justify-content: center;
        line-height: 1.4;
        padding: 8px;
        text-decoration: none;
        width: 72px;
        ${isActive === true ? 'background: hsl(var(--primary))' : isDisabled === true ? 'background: hsl(var(--menu-primary) / 0.6)' : ''};
        &:hover {
            ${isActive === true ? 'background: hsl(var(--primary))' : isDisabled === true ? 'background: hsl(var(--menu-primary) / 0.6)' : 'background: hsl(var(--primary) / 0.1)'};
        }

        &:not(:last-of-type) {
            margin-bottom: 4px;
        }

        svg {
            path {
                ${isActive === true ? 'stroke: hsl(var(--menu-secondary));' : 'stroke: hsl(var(--menu-primary));'}
            }
        }
    `;
};

export const MenuItemWrapper = styled(Link, { label: 'MenuItemWrapper' })<MenuButtonWrapperType>`
    ${({ isActive, isDisabled }): SerializedStyles => setMenuItem(isActive, isDisabled)};
`;

export const UserMenuItemWrapper = styled('div', { label: 'UserMenuItemWrapper' })<MenuButtonWrapperType>`
    position: relative;
    ${({ isActive }): SerializedStyles => setMenuItem(isActive)};
`;

export const PlaybookIconWrapper = styled('div', { label: 'PlaybookIconWrapper' })`
    ${setMenuItem()}
    cursor: default;

    &:hover {
        background: transparent;
        border: none;
    }
`;

export const Container = styled('div', { label: 'Container' })`
    -webkit-overflow-scrolling: touch;
    align-items: center;
    display: flex;
    flex-flow: column nowrap;
    flex: 1 1 auto;
    max-height: calc(100vh - 360px);
    overflow-x: hidden;
    overflow-y: auto;
    scrollbar-color: hsl(var(--border)) ${scrollbar.bgColorSecondary};
    scrollbar-width: thin;

    &::-webkit-scrollbar {
        background: ${scrollbar.bgColorSecondary};
        width: 4px;
    }

    &::-webkit-scrollbar-track {
        background: salmon;
        border-radius: 8px;
        width: 4px;
    }

    &::-webkit-scrollbar-thumb {
        width: 4px;
        background-color: hsl(var(--border));
        border-radius: 8px;
        border: 2px solid transparent;
        background-clip: padding-box;
    }

    &::-webkit-scrollbar-thumb:hover {
        background-color: hsl(var(--border));
    }
`;

export const MainMenuContainer = styled('div', { label: 'MainMenuContainer' })`
    flex: 1 1 auto;
    padding: 24px 0;
    position: relative;
    &::before,
    &::after {
        background: rgba(228, 228, 231, 0.50);
        content: '';
        height: 1px;
        left: 0;
        margin: 0 auto;
        position: absolute;
        right: 0;
        width: 32px;
    }
    &::before {
        top: 12px;
    }
    &::after {
        bottom: 12px;
    }
`;

export const MenuMainFooter = styled('div', { label: 'MenuMainFooter' })`
    display: flex;
    flex-flow: column nowrap;
    justify-content: center;
    align-items: center;
`;

const setIcon = (): SerializedStyles => {
    return css`
        flex: 0 0 28px;
        width: 28px;
        margin-bottom: 4px;
    `;
};

export const PlaybookLogo = styled(PlayBookIcon, { label: 'PlaybookLogo' })`
    ${setIcon};
`;

export const IconTradingWrapper = styled(TradingIcon, { label: 'IconTradingWrapper' })`
    ${setIcon};
`;

export const IconComplianceWrapper = styled(ComplianceIcon, { label: 'IconComplianceWrapper' })`
    ${setIcon};
`;

export const IconReportingWrapper = styled(ReportingIcon, { label: 'IconReportingWrapper' })`
    ${setIcon};
`;

export const IconAdminWrapper = styled(AdminIcon, { label: 'IconAdminWrapper' })`
    ${setIcon};
`;

export const IconCMSWrapper = styled(CMSIcon, { label: 'IconCMSWrapper' })`
    ${setIcon};
`;

export const IconCasinoWrapper = styled(CasinoIcon, { label: 'IconCasinoWrapper' })`
    ${setIcon};
`;

export const IconBonusEngineWrapper = styled(BonusEngineIcon, { label: 'IconBonusEngineWrapper' })`
    ${setIcon};
`;

export const IconNotificationWrapper = styled(NotificationIcon, { label: 'IconNotificationWrapper' })`
    ${setIcon};
`;

export const IconUserWrapper = styled(UserIcon, { label: 'IconUserWrapper' })`
    ${setIcon};
`;

export const IconNotificationsWrapper = styled(BellIcon, { label: 'IconNotificationsWrapper' })`
    ${setIcon};
`;


export const IconLogoutWrapper = styled(LogoutIcon, { label: 'IconLogoutWrapper' })`
    ${setIcon};
`;
